import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Moment } from 'moment';
import type { RootState } from '../../store';

interface Props {
  orderFlowNeedsRefresh: boolean;
  serviceMonitorSummaryNeedsRefresh: boolean;
  serviceLogSummaryNeedsRefresh: boolean;
  lastUpdatedChart: Moment | null;
  lastUpdatedSummary: Moment | null;
  lastUpdatedServiceStatus: Moment | null;
  lastUpdatedServiceLog: Moment | null;
}

// Define the initial state using that type
const initialState: Props = {
  orderFlowNeedsRefresh: true,
  serviceMonitorSummaryNeedsRefresh: true,
  serviceLogSummaryNeedsRefresh: true,
  lastUpdatedChart: null,
  lastUpdatedSummary: null,
  lastUpdatedServiceStatus: null,
  lastUpdatedServiceLog: null,
};

export const utilsSlice = createSlice({
  name: 'utils',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    refreshOrderFlow: (state, action: PayloadAction<boolean>) => {
      state.orderFlowNeedsRefresh = action.payload;
    },
    refreshServiceMonitorSummary: (state, action: PayloadAction<boolean>) => {
      state.serviceMonitorSummaryNeedsRefresh = action.payload;
    },
    refreshServiceLogSummary: (state, action: PayloadAction<boolean>) => {
      state.serviceLogSummaryNeedsRefresh = action.payload;
    },
    setLastUpdatedChart: (state, action: PayloadAction<Moment>) => {
      state.lastUpdatedChart = action.payload;
    },
    setLastUpdatedSummary: (state, action: PayloadAction<Moment>) => {
      state.lastUpdatedSummary = action.payload;
    },
    setLastUpdatedServiceStatus: (state, action: PayloadAction<Moment>) => {
      state.lastUpdatedServiceStatus = action.payload;
    },
    setLastUpdatedServiceLog: (state, action: PayloadAction<Moment>) => {
      state.lastUpdatedServiceLog = action.payload;
    },
  },
});

export const {
  refreshOrderFlow,
  refreshServiceMonitorSummary,
  refreshServiceLogSummary,
  setLastUpdatedChart,
  setLastUpdatedSummary,
  setLastUpdatedServiceStatus,
  setLastUpdatedServiceLog,
} = utilsSlice.actions;

// Other code such as selectors can use the imported `RootState` type
export const selectOrderFlowRefresh = (state: RootState) => state.utils.orderFlowNeedsRefresh;
export const selectServiceMonitorSummaryRefresh = (state: RootState) =>
  state.utils.serviceMonitorSummaryNeedsRefresh;
export const selectServiceLogSummaryRefresh = (state: RootState) =>
  state.utils.serviceLogSummaryNeedsRefresh;
export const selectLastUpdatedChart = (state: RootState) => state.utils.lastUpdatedChart;
export const selectLastUpdatedSummary = (state: RootState) => state.utils.lastUpdatedSummary;
export const selectLastUpdatedServiceStatus = (state: RootState) =>
  state.utils.lastUpdatedServiceStatus;
export const selectLastUpdatedServiceLog = (state: RootState) => state.utils.lastUpdatedServiceLog;

export default utilsSlice.reducer;
